/** Knutten swarm to the button under the pointer, more and more of them. A click smacks them into red dots. */
const BUTTONS = ".button, [type='submit']:not(:disabled)";
const WING_COLORS = ['#ffffff', '#ffffff', '#a9dcec', '#c3b6dc', '#f1eda6', '#c9a3b0', '#cfcfcf'];
const MAX_FLIES = 140;
const SPLAT_SECONDS = 1.6;

const flies = [];
let canvas;
let context;
let target = null;
let hoverSeconds = 0;
let lastFrame = 0;
let running = false;

const random = (min, max) => min + Math.random() * (max - min);

const resize = () => {
  const ratio = window.devicePixelRatio || 1;
  canvas.width = window.innerWidth * ratio;
  canvas.height = window.innerHeight * ratio;
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
};

const setup = () => {
  canvas = document.createElement('canvas');
  canvas.className = 'knutten';
  canvas.setAttribute('aria-hidden', 'true');
  context = canvas.getContext('2d');
  resize();
  window.addEventListener('resize', resize);
};

// A new knut flies in from a random edge of the window.
const spawn = () => {
  const edge = Math.floor(random(0, 4));
  const along = Math.random();
  flies.push({
    x: edge === 1 ? window.innerWidth + 20 : edge === 3 ? -20 : along * window.innerWidth,
    y: edge === 0 ? -20 : edge === 2 ? window.innerHeight + 20 : along * window.innerHeight,
    vx: 0,
    vy: 0,
    size: random(0.6, 1.3),
    color: WING_COLORS[Math.floor(random(0, WING_COLORS.length))],
    // Where around the button this knut circles.
    angle: random(0, Math.PI * 2),
    spin: random(0.5, 2) * (Math.random() < 0.5 ? -1 : 1),
    distance: random(-8, 45),
    flutter: random(0, Math.PI * 2),
    escape: random(0, Math.PI * 2),
    dead: false,
    age: 0,
  });
};

const move = (fly, seconds) => {
  if (target) {
    const box = target.getBoundingClientRect();
    const goalX = box.left + box.width / 2 + Math.cos(fly.angle) * (box.width / 2 + fly.distance);
    const goalY = box.top + box.height / 2 + Math.sin(fly.angle) * (box.height / 2 + fly.distance);
    fly.angle += fly.spin * seconds;
    fly.vx += (goalX - fly.x) * 18 * seconds;
    fly.vy += (goalY - fly.y) * 18 * seconds;
  } else {
    fly.vx += Math.cos(fly.escape) * 900 * seconds;
    fly.vy += Math.sin(fly.escape) * 900 * seconds;
  }

  // The nervous zigzag of a knut.
  fly.vx += random(-900, 900) * seconds;
  fly.vy += random(-900, 900) * seconds;

  const drag = Math.exp(-4 * seconds);
  fly.vx *= drag;
  fly.vy *= drag;
  fly.x += fly.vx * seconds;
  fly.y += fly.vy * seconds;
  fly.flutter += 60 * seconds;
};

const isGone = (fly) => {
  if (fly.dead) {
    return fly.age > SPLAT_SECONDS;
  }
  const outside =
    fly.x < -40 || fly.y < -40 || fly.x > window.innerWidth + 40 || fly.y > window.innerHeight + 40;
  return !target && outside;
};

const drawFly = (fly) => {
  const wing = 0.6 + 0.4 * Math.abs(Math.sin(fly.flutter));

  context.save();
  context.translate(fly.x, fly.y);
  context.rotate(Math.max(-0.5, Math.min(0.5, fly.vx / 600)));
  context.scale(fly.size, fly.size);

  context.fillStyle = '#1d1d1b';
  context.beginPath();
  context.moveTo(-2, 1);
  context.lineTo(2, 1);
  context.lineTo(0, 7);
  context.fill();

  context.fillStyle = fly.color;
  [-1, 1].forEach((side) => {
    context.beginPath();
    context.moveTo(side * 2 * wing, -7);
    context.lineTo(side * 9 * wing, 1);
    context.lineTo(side, 0);
    context.fill();
  });
  context.fillRect(-0.75, 3, 1.5, 1.5);

  context.restore();
};

const drawSplat = (fly, color) => {
  context.globalAlpha = Math.min(1, (SPLAT_SECONDS - fly.age) / 0.5);
  context.fillStyle = color;
  context.beginPath();
  context.arc(fly.x, fly.y, 4 * fly.size, 0, Math.PI * 2);
  context.fill();
  context.globalAlpha = 1;
};

const frame = (now) => {
  const seconds = Math.min(0.05, (now - lastFrame) / 1000);
  lastFrame = now;

  if (target && !target.isConnected) {
    target = null;
  }

  // The longer the pointer stays, the bigger the swarm.
  if (target) {
    hoverSeconds += seconds;
    const wanted = Math.min(MAX_FLIES, 3 + 4 * hoverSeconds + 3 * hoverSeconds * hoverSeconds);
    while (flies.length < wanted) {
      spawn();
    }
  }

  const red = getComputedStyle(document.documentElement).getPropertyValue('--color-highlight') || '#d8232a';
  context.clearRect(0, 0, window.innerWidth, window.innerHeight);

  for (let index = flies.length - 1; index >= 0; index -= 1) {
    const fly = flies[index];

    if (fly.dead) {
      fly.age += seconds;
    } else {
      move(fly, seconds);
    }

    if (isGone(fly)) {
      flies.splice(index, 1);
    } else if (fly.dead) {
      drawSplat(fly, red);
    } else {
      drawFly(fly);
    }
  }

  if (target || flies.length > 0) {
    requestAnimationFrame(frame);
  } else {
    running = false;
  }
};

const start = () => {
  if (!canvas) {
    setup();
  }
  // An open popup sits above the page, so the swarm has to live inside it.
  const layer = target.closest('dialog') || document.body;
  if (canvas.parentNode !== layer) {
    layer.append(canvas);
  }
  if (!running) {
    running = true;
    lastFrame = performance.now();
    requestAnimationFrame(frame);
  }
};

const buttonOf = (node) => (node instanceof Element ? node.closest(BUTTONS) : null);

document.addEventListener('pointerover', (event) => {
  const button = buttonOf(event.target);
  // Touch has no hover; moving within the same button changes nothing.
  if (event.pointerType !== 'mouse' || !button || button === buttonOf(event.relatedTarget)) {
    return;
  }
  target = button;
  hoverSeconds = 0;
  start();
});

document.addEventListener('pointerout', (event) => {
  if (target && buttonOf(event.relatedTarget) !== target) {
    target = null;
  }
});

// Smack: every knut becomes a red dot, and the swarm stays away until the next hover.
document.addEventListener('pointerdown', (event) => {
  if (!target || buttonOf(event.target) !== target) {
    return;
  }
  flies.forEach((fly) => {
    fly.dead = true;
  });
  target = null;
});
